import { AgGridEvent, ApplyColumnStateParams, CellClickedEvent, CellKeyDownEvent, ColGroupDef, Column, IRowNode, ManagedGridOptionKey, RowSelectedEvent, SideBarDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional theme CSS
import 'ag-grid-enterprise';
import JsPDF from "jspdf";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IsGroupOpenByDefaultParams, IsRowSelectable, RefreshCellsParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { RefObject, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Spinner from '../../components/Spinner';
import { useDebounce } from '../../hooks/useDebounce';
import { TestInterface } from '../../pages/reports/TestReport';
import BaseCellRenderer from './CellRenderers/BaseCellRenderer';
import DecimalCellRenderer from './CellRenderers/DecimalCellRenderer';
import IntegerCellRenderer from './CellRenderers/IntegerCellRenderer';
import PercentCellRenderer from './CellRenderers/PercentCellRenderer';
import PriceCellRenderer from './CellRenderers/PriceCellRenderer';
import ValidCellRenderer from './CellRenderers/ValidationCellRenderer';
import { AnyFilter, Filter, FilterForwardRef, FilterState } from './FilterComponents/Filters';
import { Search, SearchForwardRef, SearchState } from './FilterComponents/Search';
import { Pagination, PaginationForwardRef, PaginationState } from './Pagination';
import autoTable, { RowInput } from 'jspdf-autotable';
import { v4 as uuidv4 } from 'uuid';
import { HasProp, NonEmptyArray } from 'helpers/utility/types';
import LinkCellRenderer from './CellRenderers/LinkCellRenderer';

type RowDataItem = {
    [key: string]: any;
}

export enum ColumnType {
    String,
    Number,
    Price = 'priceColumn',
}

// The state that gets passed to callback supplied by page.
export type GridState = {
    paginationState?: PaginationState;
    filterState?: FilterState;
    searchState?: SearchState;
}

export enum AgGridRowHeight {
    Normal,
    Slim,
    Compact,
}

type AgGridProps = {
    gridInterface?: TestInterface<unknown>;
    rowData: RowDataItem[];
    columnDefs?: ZColDef<any>[];
    loading: boolean;
    sideBar?: SideBarDef;
    gridOptions?: GridOptions<any>;
    onCellClicked?: (e: CellClickedEvent) => void;
    onCellEditingStopped?: (rowId: string, colId: string, newValue: any) => void;
    pagination?: "none" | "clientside" | {type: "serverside", totalRows: number };
     /** @deprecated The property should not be used */ //TODO: Remove this prop and anything it relies on, we need to integrate with AgGrid filtering/sorting properly
    handleSubmit?: (g: GridState) => void;
    heightPx?: number;
    searchText?: string;
    rowHeight?: AgGridRowHeight;
    rowGrouping?: RowGroupTree<any>;
    filters?: AnyFilter[];
    search?: boolean;
    containerStyle?: React.CSSProperties;
    rowSelectionType?: 'single' | 'multiple',
    getRowId?: (params: GetRowIdParams<any, any>) => string,
    isGroupOpenByDefault?: (params: IsGroupOpenByDefaultParams) => boolean,
    onSelectionChanged?: (selectedRows: RowDataItem[]) => void
    onRowSelected?: (ev: RowSelectedEvent) => void;
    isRowSelectable?: IsRowSelectable;
}


export interface ISelectRowOnClickParams<TData = any> {
    /** The data in the row */
    data: TData;
} 

export interface ZColDef<TData = any> extends ColDef<TData> {
    /** When a cell in this column is clicked, trigger row selection */
    selectRowOnClick?: boolean | ((params: ISelectRowOnClickParams<TData>) => boolean);
}

const GridLoaderComponent = (): JSX.Element => {
    return (
        <Spinner className="spinner-border-md me-1" tag="span" color="primary" />
    );
}

export interface AgGridRef {
    ExportToExcel: () => void;
    autoSizeColumns: () => void;
    clearRowSelection: () => void;
    refreshCells: (params: RefreshCellsParams) => void;
    getSelectedRows: () => RowDataItem[] | undefined;
    setGridOption: (opt: ManagedGridOptionKey, value: any) => void;
    getAllRowNodes: () => IRowNode[];
    setCellData: (rowNode: IRowNode, col: string, value: any) => void;
    exportToFile: (fileType: "csv" | "xlsx" | "pdf", fileName: string, ignoreColumns?: string[]) => void;
    getGridRef: () => RefObject<AgGridReact>;
    updateColumnDefinitions: (colDefs: ZColDef<unknown>[]) => void;
}

function GetRowHeight(rowHeight?: AgGridRowHeight): number | undefined {
    switch(rowHeight) {
        case AgGridRowHeight.Slim:
            return 32;
        case AgGridRowHeight.Compact:
            return 24;
        case AgGridRowHeight.Normal:
        default:
            return undefined;
    }
}

// the new type to be returned by the flattenRowGroupTree function, must contain a key string and also arbitrary _g0, _g1.... properties for each group in the path to the leaf node (including the leaf node)
// I want this type to contain a key of type string and then arbitrary _g0, _g1, ... properties that are of type RowGroupTree<T>
type FlattenedRowGroup<T> = { key: string, path: RowGroupTree<T>[] };

type RowGroupBase<T> = Pick<ZColDef, "cellRendererSelector"> & {
    group: keyof T | string;
    isField?: boolean;
}

type Node<T> =  {
    children: NonEmptyArray<RowGroupTree<T>>; // By enforcing there is at least 1 child, we are forcing each path in the RowGroupTree to end with a RowGroupLeaf
}

type Leaf<T> = {
    key?: keyof T;
}

// We want the properties in the node to be mutually exclusive to the properties in the leaf, so we will use the following type definitions to enforce this
// If they aren't mutually exclusive, then the properties should be in the RowGroupBase type
export type RowGroupNode<T> = RowGroupBase<T> & Node<T> & {[K in keyof Leaf<T>]?: never }
export type RowGroupLeaf<T> = RowGroupBase<T> & Leaf<T> & {[K in keyof Node<T>]?: never };
export type RowGroupTree<T> = RowGroupNode<T> | RowGroupLeaf<T>;

function flattenRowGroupTree<T>(rowGroup: RowGroupTree<T>): FlattenedRowGroup<T>[] {
    // This method flattens the row group tree into a list of a FlattendRowGroup that will allow us to easily append these to the rowData,
    // We want to use the FlattenedRowGroup type because we want both the key from the leaf node but also all groups along the path to the leaf in an array
    // The function will return an array of these FlattenedRowGroup objects
    const flattenedRowGroups: FlattenedRowGroup<T>[] = [];

    // This function will be called recursively to build up the FlattenedRowGroup objects
    function __flattenRowGroupTreeRecursive(rowGroup: RowGroupTree<T>, currentPath: FlattenedRowGroup<T>): void {
        // If we are at a leaf node, we need to add the currentPath to the flattenedRowGroups array
        if (rowGroup.children && rowGroup.children.length === 0) {
            flattenedRowGroups.push(currentPath);
        } else {
            // If we are at a node, we need to go through each child and call this function recursively
            rowGroup.children?.forEach((child) => {
                const newPath = { path: [...currentPath.path, child], key: String(child.key) };
                __flattenRowGroupTreeRecursive(child, newPath);
            });
        }
    }

    // Start the recursive function
    __flattenRowGroupTreeRecursive(rowGroup, { key: '', path: [] });

    return flattenedRowGroups;
}

// This method will take the rowData and the rowGroup and append the rowGroup to the rowData by first flattening the row group tree and then adding the _g0, _g1, etc. properties to the rowData
export function appendRowGroupToRowData<T>(rowData: HasProp<T, "key">[], rowGroup: RowGroupTree<T>): HasProp<T | { path: string[] }, "key">[] {
    const flattenedRowData: FlattenedRowGroup<HasProp<T, "key">>[] = flattenRowGroupTree(rowGroup);
    const newRowData: HasProp<T | { path: string[] }, "key">[] = [...rowData].map((row) => {
        let newRow = { ...row };
        // Use the key property to find the appropriate row grouping for the current property
        const rowGroup = flattenedRowData.find((flattenedRow) => flattenedRow.key != null && row.key != null && flattenedRow.key === row.key);

        // If we found a row group, we need to add all the groups in the path as a list to the newRow, we need to check the isField property on each of these to determine if the group string is actually just a key of the rowData
        if (rowGroup) {
            // Map row group data and return in a way that we can then append to the new row.
            const mappedRowGroup = rowGroup.path.map((p) => p.isField ? newRow[p.group as keyof T] : p.group);
            // Merge the new row with the mapped row group
            return { ...newRow, path: [...mappedRowGroup] };
        }

        return newRow;
    });

    // For each flattened row that doesn't have a key, this means the user wants to create a blank row for this grouping (ie the group will have no data).
    // So we will add this as a new row to the newRowData
    flattenedRowData.filter((flattenedRow) => flattenedRow.key == null).forEach((flattenedRow) => {
        const mappedRowGroup = flattenedRow.path.map((p) => p.group as string);
        const mappedRow: HasProp<T | { path: string[] }, "key"> = { key: null, path: [...mappedRowGroup] };
        newRowData.push(mappedRow);
    });

    return newRowData;
}

// NOTE: AgGrid already supports Tree Data styled row grouping automatically, so we don't need to implement our own column definition generation logic, but leaving it here commented out just in case we need it in the future
// // Original plan was to automatically add these column definitions but why not just expose this function to the dev and let them managed it themselves
// export function rowGroupTreeToColDefs<T>(rowGroupTree: RowGroupTree<T>): ZColDef[] {
//     const maxDepth = MapTreeNodes(rowGroupTree, 0);

//     let rowGroupColDefs: ZColDef[] = [];

//     const depthNodeMap: Map<number, RowGroupTree<T>[]> = new Map<number, RowGroupTree<T>[]>();

//     // This function returns the max depth for convenience
//     function MapTreeNodes<T>(rowGroupTree: RowGroupTree<T>, depth: number): number {
//         let currentDepth = depth;
//         if (rowGroupTree.children && rowGroupTree.children.length > 0) {
//             currentDepth++;
//             let childrenDepths = rowGroupTree.children.map(c => {
//                 const prevValues = depthNodeMap.get(currentDepth) ?? [];
//                 //@ts-ignore
//                 depthNodeMap.set(currentDepth, [...prevValues, c]);
//                 return MapTreeNodes(c, currentDepth);
//             });
//             return Math.max(...childrenDepths);
//         } else {
//             return currentDepth;
//         }
//     }

//     for (let i = 0; i <= maxDepth; i++) {
//         rowGroupColDefs.push({
//             field: `_g${i}`,
//             rowGroup: true,
//             hide: true,
//             cellRendererSelector: (params) => {
//                 const nodes = depthNodeMap.get(i);

//                 nodes?.forEach(n => {
//                     if (n.cellRendererSelector) {
//                         if (params.node.key === n.group) {
//                             return n.cellRendererSelector(params);
//                         }
//                     }
//                 });

//                 return { component: 'baseCellRenderer' };
//             }
//         });
//     }

//     return rowGroupColDefs;
// }

export const AgGrid = forwardRef<AgGridRef, AgGridProps>((props: AgGridProps, ref) => {
    const paginationRef = useRef<PaginationForwardRef>();
    const filtersRef = useRef<FilterForwardRef>();
    const searchRef = useRef<SearchForwardRef>();
    const gridRef = useRef<AgGridReact>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const gridId: string = uuidv4();
    
    /**
    * This function iterates over all of the rows and columns to create
    * a matrix of cells
    */
    const getRowsToExport = (gridApi: GridApi, exportColumns?: Column<any>[]) => {
        let columns = exportColumns ? [...exportColumns] : undefined;

        if (!columns) {
            columns = gridApi.getAllDisplayedColumns();
        }
    
        const getCellToExport = (column: string | Column<any>, node: IRowNode<any>) => gridApi.getValue(column, node)?.toString() ?? '';
    
        const rowsToExport: any[][] = [];
        gridApi.forEachNodeAfterFilterAndSort((node) => {
            const rowToExport = columns.map((column) =>
                getCellToExport(column, node)
            );
            rowsToExport.push(rowToExport);
        });
    
        return rowsToExport;
    };
  

    useImperativeHandle(ref, () => ({
        ExportToExcel() {
            if (gridRef.current !== null){
                gridRef.current.api.exportDataAsExcel();
            }
        },

        autoSizeColumns() {
            gridRef.current?.api.autoSizeAllColumns();
        },

        clearRowSelection() {
            gridRef.current?.api.deselectAll();
        },

        refreshCells(params: RefreshCellsParams) {
            if (gridRef.current?.api) {
                console.log("Refreshing cells with params: ", params);
                gridRef.current.api.refreshCells(params);
            }
        },

        getSelectedRows() {
            return gridRef.current?.api.getSelectedRows();
        },

        getAllRowNodes() {
            let rowIds: IRowNode[] = [];
            gridRef.current?.api?.forEachNode((rowNode: IRowNode, ix: number) => {
                rowIds.push(rowNode);
            });

            return rowIds;
        },

        setCellData(rowNode: IRowNode, col: string, value: any) {
            rowNode.setDataValue(col, value);
        },

        setGridOption(opt: ManagedGridOptionKey, value: any) {
            gridRef.current?.api?.setGridOption(opt, value);
        },

        exportToFile(fileType: "csv" | "xlsx" | "pdf", fileName: string, ignoreColumns?: string[]) {
            if (!gridRef.current) return;
            let cols = gridRef.current.api.getAllDisplayedColumns()
            
            if (ignoreColumns) {
                cols = cols.filter(c => {
                    const colDef = c.getColDef();
                    return !(ignoreColumns && ignoreColumns.findIndex(s => s === colDef.field) != -1);
                });
            }
            
            const headerColumnInfo = cols.map((col: (Column<any>)) => {
                const colDef = col.getColDef();
                 return { name: colDef.headerName ?? "", key: col.getId(), column: col } 
            });

            switch(fileType) {
                case "csv":
                    gridRef.current.api.exportDataAsCsv({ fileName: fileName, columnKeys: headerColumnInfo.map(c => c.key) });
                    break;
                case "xlsx":
                    gridRef.current.api.exportDataAsExcel({ fileName: fileName, author: "Z Freedom", sheetName: "Export", columnKeys: headerColumnInfo.map(c => c.key) });
                    break;
                case "pdf":
                    const doc = new JsPDF("landscape");
                    const headerNames = headerColumnInfo.map(colInfo => colInfo.name);
                    const rowData = getRowsToExport(gridRef.current.api, headerColumnInfo.map(colInfo => colInfo.column));
                    autoTable(doc, {
                        head: [headerNames],
                        body: rowData,
                        margin: { top: 20 },
                        styles: {
                            minCellHeight: 9,
                            halign: "left",
                            fontSize: 11,
                        },
                    });
                    doc.save(`${fileName}.pdf`);
                    break;
                default:
                    throw "Somehow the file type isn't equal to one that is compatible!";
            }
        },
        getGridRef() {
            return gridRef;
        },
        updateColumnDefinitions(colDefs: ZColDef<unknown>[]) {
            gridRef.current?.api?.setGridOption("columnDefs", colDefs);
        }
      }));

    // Auto size the columns so we don't have "..." in the column headers
    function autoSizeAll(e: AgGridEvent<any>) {
        e?.api?.autoSizeAllColumns();
    }

    const debouncedAutosize = useDebounce(autoSizeAll, 100);
    
    const columnTypes = useMemo<{
        [key: string]: ColDef
    }>(() => {
        return {
            priceColumn: {
                onCellValueChanged: (e) => {
                    // Ensures value that is saved is truncated to 2 dp.
                    e.node?.setDataValue(e.column.getColId(), e.newValue ? parseFloat(e.newValue.formatAsPrice()) : null);
                },
                cellRenderer: 'priceCellRenderer'
            },
            
            decimalColumn: {
                onCellValueChanged: (e) => {
                    // Ensures value that is saved is truncated to 2 dp.
                    e.node?.setDataValue(e.column.getColId(), e.newValue ? parseFloat(e.newValue.formatAsPrice()) : null);
                },
                cellRenderer: 'decimalCellRenderer'
            }
        }
    }, []);

    useEffect(() => {
        if (gridApi) {
            if (props.loading) {
                gridApi.showLoadingOverlay();
            } else {
                if (props.rowData.length > 0) {
                    gridApi.hideOverlay();
                } else {
                    gridApi.showNoRowsOverlay();
                }
            }
        }
    }, [props.loading, gridApi]);

    useEffect(() => {
        const handleWindowResize = (event: any) => {
            debouncedAutosize(event);
        };

        // Add event listener
        window.addEventListener('resize', handleWindowResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    function onPaginate(p: PaginationState) {
        props.handleSubmit && props.handleSubmit({ paginationState: p, filterState: (props.filters && filtersRef.current) ? filtersRef.current.getAppliedFilters() : undefined, searchState: (props.search && searchRef.current) ? searchRef.current.getAppliedSearchState() : undefined });
    }
    
    function onFilter(f: FilterState) {
        // When filter is applied we need to reset pagination state
        paginationRef.current?.resetPagination(true);
        props.handleSubmit && props.handleSubmit({ paginationState: props.pagination && paginationRef.current ? {offset: 0, limit: paginationRef.current.getPagination().limit } : undefined, filterState: f, searchState: (props.search && searchRef.current) ? searchRef.current.getSearchState() : undefined });
    }

    function onSearch(searchState: SearchState) {
        filtersRef.current?.applyFilters();
        // Will need to call a callback on page
        paginationRef.current?.resetPagination(true);
        props.handleSubmit && props.handleSubmit({ paginationState: props.pagination && paginationRef.current ? {offset: 0, limit: paginationRef.current.getPagination().limit } : undefined, filterState: (props.filters && filtersRef.current) ? filtersRef.current.getFilters() : undefined, searchState: searchState });
    }

    return (
        <>
        {(props.search != null || props.filters != null) && <Row>
            {props.search ? <Col xs={6}>
                <Search
                    ref={searchRef}
                    onSearch={onSearch}
                    totalRows={( typeof props.pagination === "object") ? props.pagination.totalRows : props.rowData.length}
                />
            </Col> : <></> }
            {props.filters ? <Col xs={6}>
                <Filter
                    ref={filtersRef}
                    onFilterApplied={onFilter} 
                    filters={props.filters}
                />
                 Filters/search should go here 
            </Col> : <></>}
        </Row>}
        <div id={gridId} className="d-flex flex-column w-100 h-100">
                <AgGridReact
                    className={`ag-theme-quartz ag-theme-custom w-100 flex-grow`}
                    containerStyle={ { height: props.heightPx ?? "100%", ...props.containerStyle }}
                    loadingOverlayComponent={GridLoaderComponent}
                    ref={gridRef}
                    rowHeight={GetRowHeight(props.rowHeight)}
                    getRowId={props.getRowId}
                    onCellKeyDown={(event: CellKeyDownEvent) => {
                        if (event.event as KeyboardEvent) {
                            const kbEvent = event.event as KeyboardEvent;
                            if (kbEvent.code === "Enter" || kbEvent.code === "Space") {
                                event.colDef.onCellClicked && event.colDef.onCellClicked(event);
                            }
                        }
                    }}
                    rowData={props.rowData}
                    rowSelection={props.rowSelectionType}
                    onRowSelected={(ev) => props.onRowSelected && props.onRowSelected(ev)}
                    onSelectionChanged={(ev) => props.onSelectionChanged && props.onSelectionChanged(ev.api.getSelectedRows())}
                    suppressRowClickSelection
                    singleClickEdit
                    rowMultiSelectWithClick
                    columnDefs={props.columnDefs}
                    pagination={props.pagination && props.pagination !== "none"}
                    suppressPaginationPanel
                    groupAllowUnbalanced
                    animateRows
                    suppressContextMenu
                    isRowSelectable={props.isRowSelectable}
                    isGroupOpenByDefault={props.isGroupOpenByDefault}
                    gridOptions={props.gridOptions}
                    quickFilterText={props.searchText}
                    onCellClicked={(e: CellClickedEvent) => {
                        // This logic is for enabling row selection by just clicking a cell but there are some cases where we don't want that to happen
                        // For example, if the cell is using an editor, that click event will conflict and the default behaviour of AgGrid is for the row selection to override the editor click event
                        // Resulting in not being able to actually edit the cell. So we allow the definition of the column to contain a selectRowOnClick to define if we want this functionality on the column
                        const selectOnClick = (e.api.getColumns()?.find(c => c.getColId() === e.column.getColId())?.getColDef() as ZColDef).selectRowOnClick;
                        const triggerSelectOnClick = selectOnClick == null ? true : (typeof selectOnClick === "boolean" ? selectOnClick : selectOnClick({ data: e.data }));
                        if (triggerSelectOnClick) {
                            const prev = e.node.isSelected();
                            e.node.setSelected(prev == null ? true : !prev);
                        }
                        props.onCellClicked && props.onCellClicked(e);
                    }}
                    onCellEditingStopped={(e) => {
                        e.node && e.node.id && props.onCellEditingStopped && props.onCellEditingStopped(e.node.id, e.column.getColId(), e.newValue);
                    }}
                    suppressNoRowsOverlay={props.loading}
                    groupDisplayType='custom'
                    suppressAggFuncInHeader={true}
                    groupRowRendererParams = {
                        {suppressCount: true}
                    }
                    defaultColDef={{
                        suppressHeaderMenuButton: true,
                        // flex: 1,
                        minWidth: 100,
                        resizable: true,
                        sortable: false,
                        lockVisible: true,
                        lockPinned: false,
                        lockPosition: true,
                        rowDrag: false,
                        cellRenderer: 'baseCellRenderer',
                    }}
                    sideBar = {props.sideBar}
                    components={{
                        baseCellRenderer: BaseCellRenderer,
                        priceCellRenderer: PriceCellRenderer, 
                        decimalCellRenderer: DecimalCellRenderer,
                        percentCellRenderer: PercentCellRenderer,
                        integerCellRenderer: IntegerCellRenderer,
                        validCellRenderer: ValidCellRenderer,
                        linkCellRenderer: LinkCellRenderer,
                    }}
                    columnTypes={columnTypes}
                    onColumnResized={(e) => {
                        // TODO: For some reason when changing an unrelated dropdown on the page,
                        // TODO: onColumnResized fires with gridOptionsChanged as the source
                        // TODO: and if there's a single column it takes up the entire grid, so we just
                        // TODO: won't autosize here, and also disabled flex to prevent further erroneous
                        // TODO: calls to autosize
                        // console.log(e.source);
                        // console.log("onColumnResized fired");
                        // if (e.source !== "gridOptionsChanged" && e.source !== "autosizeColumns") {
                        //     autoSizeAll(e);
                        // }
                    }}
                    onRowDataUpdated={(e) => {
                        debouncedAutosize(e);
                    }}
                    onRowGroupOpened={(e) => {
                        // console.log("onRowGroupOpened fired");
                        // This is so that when we drop a group down, if the children would go off the bottom of the grid out of view, it will scroll up to show them
                        e.expanded && e.node.childrenAfterGroup && e.api.ensureIndexVisible(Math.max(...e.node.childrenAfterGroup.filter(c => c.rowIndex).map(c => c.rowIndex!)));
                        debouncedAutosize(e);
                    }}
                    onGridReady={(e) => {
                        // console.log("onGridReady fired");
                        setGridApi(e.api);
                        debouncedAutosize(e);
                    }}
                    onGridSizeChanged={(e) => {
                        // console.log("onGridSizeChanged fired");
                        debouncedAutosize(e);
                    }}
                    onGridColumnsChanged={(e) => {
                        // console.log("onGridColumnsChanged fired");
                        // console.log(e.type);
                        debouncedAutosize(e);
                    }}
                    onFirstDataRendered={(e) => {
                        // console.log("onFirstDataRendered fired");
                        debouncedAutosize(e);
                    }}
            />
            <Row>
                <Col xs={12}>
                    {gridApi && props.pagination && props.pagination !== "none" && <Pagination 
                        ref={paginationRef}
                        serverSidePagination={ (typeof props.pagination === "object") ? {handlePaginationChanged: onPaginate, totalRows: props.pagination.totalRows} : undefined}
                        gridProps={gridApi}
                        pageSizes={[ 50, 100, 250, 500 ]}
                        />
                    }
                </Col>
            </Row>
        </div>
        </>
    );
});